import { useEditor, useNode } from "@craftjs/core";
import React, { useState, useRef, useContext, useMemo } from "react";
import {
  AlignCenterIcon,
  AlignContentAroundIcon,
  AlignContentBetweenIcon,
  AlignContentCenterIcon,
  AlignContentEndIcon,
  AlignContentSpaceEvenIcon,
  AlignContentTopIcon,
  AlignEndIcon,
  AlignStartIcon,
  DisplayBlockIcon,
  FlexColumnIcon,
  FlexIcon,
  FlexNoWrapIcon,
  FlexRowIcon,
  FlexWrapIcon,
  JustifyAroundIcon,
  JustifyBetweenIcon,
  JustifyCenterIcon,
  JustifyLeftIcon,
  JustifyRightIcon,
  JustifySpaceEvenIcon,
} from "../../../assets/ui-icons";
import {
  ColorInput,
  ToggleInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  Spacer,
  AlignInput,
  TextInput,
  RangeNumberInput,
  TabsV2,
  NumberInput,
  RangeInput,
  EditPopover,
  BorderRadiusInput,
  BorderInput,
  Notice,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { ImagePickerUIKit } from "../../ui-kit";
import { GrigoraDashboardContext } from "../../dashboard/context";
import SelectRadioButtonInput from "../../input/select-radio-buttons";
import { isEmpty, unescape } from "lodash";

function SourceImage({ projectID, src, srcMode, onClick }) {
  const imgsrc = useMemo(() => {
    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${projectID}/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${projectID}/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);
  console.log(srcMode);
  return (
    <div onClick={onClick} className="relative">
      <div className="absolute flex top-0 bottom-0 left-0 right-0 justify-center items-center duration-300 hover:bg-black">
        <FontAwesomeIcon icon={faEdit} />
      </div>
      <img src={imgsrc} />
    </div>
  );
}

export const SliderSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    linkedNodes,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    linkedNodes: node?.data?.linkedNodes,
    supports: node.data?.type?.craft?.related?.supports,
  }));
  const { actions, query } = useEditor();
  const {
    totalSlides = 3,
    isClickable,
    loop,
    hasDynamicBullets,
    grabCursor,
    hasPagination = true,
    paginationType,
    spaceBetween,
    spaceBetweenTablet,
    spaceBetweenMobile,
    slidePerView,
    slidePerViewTablet,
    slidePerViewMobile,
    isCentered,
    isFreeMode,
    hasEffect,
    effectType,
    hasMouseWheel,
    hasAutoplay,
    hasParallax,
    isKeyboardChange,
    hasScrollbar,
    isVertical,
    width,
    widthTablet,
    widthMobile,
    layoutWidth = "100%",
    layoutWidthTablet = "100%",
    layoutWidthMobile = "100%",
    layoutHeight = "80vh",
    layoutHeightTablet = "80vh",
    layoutHeightMobile = "80vh",
    height = "100%",
    heightMobile = "100%",
    heightTablet = "100%",
    autoplayDelay = 3000,
    disableAutoplayOnInteraction,
    parallaxBackground,
    navigation = true,
    navigationHeight,
    navigationHeightTablet,
    navigationHeightMobile,
    navigationWidth,
    navigationWidthTablet,
    navigationWidthMobile,
    navigationButtonColor,
    navigationButtonBackgroundColor,
    navigationButtonPadding,
    navigationButtonPaddingTablet,
    navigationButtonPaddingMobile,
    navigationButtonBorder,
    navigationButtonBorderRadius,
    navigationPosition,
    navigationButtonMargin,
    navigationButtonMarginTablet,
    navigationButtonMarginMobile,
    navigationSize,
    navigationAlign,
    paginationSize,
    paginationSizeTablet,
    paginationSizeMobile,
    paginationInactiveColor,
    paginationActiveColor,
    paginationBackgroundColor,
    paginationGap,
    paginationGapTablet,
    paginationGapMobile,
    paginationActiveOpacity = 1,
    paginationInactiveOpacity = 0.2,
    paginationFractionSize,
    paginationFractionSizeTablet,
    paginationFractionSizeMobile,
    paginationFractionColor,
    paginationFractionBackgroundColor,
    paginationPosition,
    sliderSpeed = 300,
    alignPagination,
    alignPaginationTablet,
    alignPaginationMobile,
    paginationBorderRadius,
    paginationBorder,
    paginationMargin,
    paginationMarginTablet,
    paginationMarginMobile,
    paginationPadding,
    paginationPaddingTablet,
    paginationPaddingMobile,
    hasStaticSlide,
    hasTouchMove = false,
    rewind,
    overflow,
    cubeEffectShadow = true,
    cubeEffectSlideShadows = true,
    coverflowEffectRotate = 50,
    coverflowEffectSlideShadows = true,
    creativeEffectPrevShadow = true,
    creativeEffectNextShadow = true,
  } = props;
  const [parallaxImagePickerModal, setParallaxImagePickerModal] =
    useState(false);
  const { projectID, blockState, setBlockState } = useContext(
    GrigoraDashboardContext,
  );

  const JUSTIFYCONTENT = [
    {
      label: "Start",
      value: "start",
      tooltip: "Start",
      icon: <AlignContentTopIcon />,
    },
    {
      label: "Center",
      value: "center",
      tooltip: "Center",
      icon: <AlignContentCenterIcon />,
    },
    {
      label: "End",
      value: "end",
      tooltip: "End",
      icon: <AlignContentEndIcon />,
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <div className="flex justify-between items-center">
            <label className="text-grigora-base mb-2">Goto Slide</label>
            <div className="flex justify-between items-center gap-3">
              <div
                onClick={(e) => {
                  let swiper = blockState(id)?.swiper;

                  if (swiper && swiper.slidePrev) {
                    swiper.slidePrev();
                  }
                }}
                className="cursor-pointer w-5 h-5 hover:bg-editor-bg-main justify-center items-center flex"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </div>
              <div
                onClick={(e) => {
                  let swiper = blockState(id)?.swiper;

                  if (swiper && swiper.slideNext) {
                    swiper.slideNext();
                  }
                }}
                className="cursor-pointer w-5 h-5 hover:bg-editor-bg-main justify-center items-center flex"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </div>
          </div>
          <RangeNumberInput
            resetValue={3}
            label="Total Slides"
            min={INPUT_LIMITS["slider-limit"].min}
            max={INPUT_LIMITS["slider-limit"].max}
            value={totalSlides}
            onChange={(val) => {
              // TODO: fix delete linked nodes of slider

              // let tempTotalSlides = totalSlides;
              // let diff = tempTotalSlides - val;

              // delete linked nodes
              // if (val < totalSlides) {
              //   for (let i = 0; i < diff; i++) {
              //     var sliderID = `slide-${id}-${tempTotalSlides - i - 1}`;

              //     console.log(
              //       "slide id",
              //       `slide-${id}-${tempTotalSlides - i - 1}`,
              //     );

              //     if (linkedNodes[sliderID]) {
              //       console.log("deleting");
              //       actions.delete(sliderID, true);
              //     }
              //   }
              // }

              setProp((props) => {
                props.totalSlides = val;
              });
            }}
          />

          <ToggleInput
            resetValue={true}
            value={navigation}
            onChange={(val) => {
              setProp((props) => {
                props.navigation = val;
              });
            }}
            label={"Has Navigation"}
          />
          {navigation && (
            <>
              <EditPopover
                label={"Navigation"}
                contentWidth={300}
                canReset={!isEmpty(navigationPosition)}
                onReset={() => {
                  setProp((props) => {
                    props.navigationPosition = undefined;
                  });
                }}
              >
                <UnitInput
                  resetValue={undefined}
                  value={navigationSize}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationSize = val;
                    });
                  }}
                  label={"Size"}
                />
                <UnitInput
                  resetValue={undefined}
                  isResponsive={true}
                  value={navigationWidth}
                  valueTablet={navigationWidthTablet}
                  valueMobile={navigationWidthMobile}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationWidth = val;
                    });
                  }}
                  setValueTablet={(val) => {
                    setProp((props) => {
                      props.navigationWidthTablet = val;
                    });
                  }}
                  setValueMobile={(val) => {
                    setProp((props) => {
                      props.navigationWidthMobile = val;
                    });
                  }}
                  label={"Width"}
                />

                <UnitInput
                  resetValue={undefined}
                  value={navigationHeight}
                  isResponsive={true}
                  valueTablet={navigationHeightTablet}
                  valueMobile={navigationHeightMobile}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationHeight = val;
                    });
                  }}
                  setValueTablet={(val) => {
                    setProp((props) => {
                      props.navigationHeightTablet = val;
                    });
                  }}
                  setValueMobile={(val) => {
                    setProp((props) => {
                      props.navigationHeightMobile = val;
                    });
                  }}
                  label={"Height"}
                />
                <UnitInput
                  resetValue={undefined}
                  value={navigationPosition?.top}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationPosition = {
                        ...(navigationPosition ? navigationPosition : {}),
                        top: val,
                      };
                    });
                  }}
                  label={"Top"}
                />

                {/* <UnitInput
                  resetValue={undefined}
                  value={navigationPosition?.bottom}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationPosition = {
                        ...(navigationPosition ? navigationPosition : {}),
                        bottom: val,
                      };
                    });
                  }}
                  label={"Bottom"}
                /> */}
                <SelectRadioButtonInput
                  layout="newline"
                  resetValue={undefined}
                  value={navigationAlign}
                  onChange={(val) => {
                    setProp((props) => {
                      props.navigationAlign = val;
                    });
                  }}
                  options={JUSTIFYCONTENT}
                  label="Align"
                />
              </EditPopover>
            </>
          )}
          <ToggleInput
            resetValue={undefined}
            value={loop}
            onChange={(val) => {
              setProp((props) => {
                if (!val) {
                  props.rewind = false;
                }
                props.loop = val;
              });
            }}
            label={"Loop"}
          />
          {!loop && (
            <ToggleInput
              resetValue={true}
              value={rewind}
              onChange={(val) => {
                setProp((props) => {
                  props.rewind = val;
                });
              }}
              label={"Rewind"}
            />
          )}

          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={spaceBetween}
            valueTablet={spaceBetweenTablet}
            valueMobile={spaceBetweenMobile}
            onChange={(val) => {
              setProp((props) => {
                props.spaceBetween = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.spaceBetweenTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.spaceBetweenMobile = val;
              });
            }}
            units={[
              { label: "px", value: "px" },
              { label: "%", value: "%" },
            ]}
            label={"Space Between"}
          />
          <NumberInput
            min={INPUT_LIMITS["slider-speed"].min}
            max={INPUT_LIMITS["slider-speed"].max}
            step={INPUT_LIMITS["slider-speed"].step}
            resetValue={300}
            value={sliderSpeed}
            onChange={(val) => {
              setProp((props) => {
                props.sliderSpeed = val;
              });
            }}
            label={"Speed (ms)"}
          />
          <ToggleInput
            resetValue={false}
            value={hasTouchMove}
            onChange={(val) => {
              setProp((props) => {
                props.hasTouchMove = val;
              });
            }}
            label={"Touch Move"}
          />
          {hasTouchMove && (
            <Notice status="warning" text="Touch Move only works in frontend" />
          )}

          <ToggleInput
            resetValue={false}
            value={hasPagination}
            onChange={(val) => {
              setProp((props) => {
                props.hasPagination = val;
              });
            }}
            label={"Has Pagination"}
          />

          {hasPagination && (
            <>
              <EditPopover
                label={"Pagination"}
                contentWidth={300}
                canReset={
                  paginationType ||
                  hasDynamicBullets ||
                  isClickable ||
                  alignPagination ||
                  paginationSize ||
                  paginationGap ||
                  paginationActiveOpacity !== 1 ||
                  paginationInactiveOpacity !== 0.2 ||
                  paginationFractionSize
                }
                onReset={() => {
                  setProp((props) => {
                    props.paginationType = undefined;
                    props.hasDynamicBullets = undefined;
                    props.isClickable = undefined;
                    props.alignPagination = undefined;
                    props.paginationSize = undefined;
                    props.paginationActiveOpacity = 1;
                    props.paginationInactiveOpacity = 0.2;
                    props.paginationFractionSize = undefined;
                    props.paginationGap = undefined;
                  });
                }}
              >
                <SelectInput
                  resetValue={undefined}
                  value={paginationType}
                  onChange={(val) => {
                    setProp((props) => {
                      props.paginationType = val;
                    });
                  }}
                  options={[
                    {
                      label: "Default",
                      value: "pagination-default",
                    },
                    {
                      label: "Page 1 of N",
                      value: "pagination-fraction",
                    },
                    {
                      label: "Show Progress",
                      value: "pagination-progress",
                    },
                  ]}
                  label="Pagination Type"
                />
                {paginationType === "pagination-default" ||
                  (!paginationType && (
                    <ToggleInput
                      resetValue={false}
                      value={hasDynamicBullets}
                      onChange={(val) => {
                        setProp((props) => {
                          props.hasDynamicBullets = val;
                        });
                      }}
                      label={"Dynamic Bullets"}
                    />
                  ))}
                {paginationType !== "pagination-progress" && (
                  <>
                    {(paginationType === "pagination-default" ||
                      !paginationType) && (
                      <>
                        <ToggleInput
                          resetValue={false}
                          value={isClickable}
                          onChange={(val) => {
                            setProp((props) => {
                              props.isClickable = val;
                            });
                          }}
                          label={"Clickable"}
                        />
                      </>
                    )}
                    <AlignInput
                      isResponsive={true}
                      value={alignPagination}
                      valueTablet={alignPaginationTablet}
                      valueMobile={alignPaginationMobile}
                      onChange={(val) => {
                        setProp((props) => {
                          props.alignPagination = val;
                        });
                      }}
                      setValueTablet={(val) => {
                        setProp((props) => {
                          props.alignPaginationTablet = val;
                        });
                      }}
                      setValueMobile={(val) => {
                        setProp((props) => {
                          props.alignPaginationMobile = val;
                        });
                      }}
                      label={"Align"}
                    />
                  </>
                )}
                {hasPagination && (
                  <>
                    {paginationType === "pagination-default" ||
                    !paginationType ? (
                      <>
                        <UnitInput
                          resetValue={undefined}
                          isResponsive={true}
                          value={paginationSize}
                          valueTablet={paginationSizeTablet}
                          valueMobile={paginationSizeMobile}
                          onChange={(val) => {
                            setProp((props) => {
                              props.paginationSize = val;
                            });
                          }}
                          setValueTablet={(val) => {
                            setProp((props) => {
                              props.paginationSizeTablet = val;
                            });
                          }}
                          setValueMobile={(val) => {
                            setProp((props) => {
                              props.paginationSizeMobile = val;
                            });
                          }}
                          label={"Size"}
                        />
                        <UnitInput
                          resetValue={undefined}
                          isResponsive={true}
                          value={paginationGap}
                          valueTablet={paginationGapTablet}
                          valueMobile={paginationGapMobile}
                          onChange={(val) => {
                            setProp((props) => {
                              props.paginationGap = val;
                            });
                          }}
                          setValueTablet={(val) => {
                            setProp((props) => {
                              props.paginationSizeTablet = val;
                            });
                          }}
                          setValueMobile={(val) => {
                            setProp((props) => {
                              props.paginationSizeMobile = val;
                            });
                          }}
                          label={"Gap"}
                        />

                        <RangeInput
                          resetValue={1}
                          min={INPUT_LIMITS["slider-bullet-opacity"].min}
                          max={INPUT_LIMITS["slider-bullet-opacity"].max}
                          step={INPUT_LIMITS["slider-bullet-opacity"].step}
                          value={paginationActiveOpacity}
                          onChange={(val) => {
                            setProp((props) => {
                              props.paginationActiveOpacity = val;
                            });
                          }}
                          label={"Active Opacity"}
                        />
                        <RangeInput
                          resetValue={0.2}
                          min={INPUT_LIMITS["slider-bullet-opacity"].min}
                          max={INPUT_LIMITS["slider-bullet-opacity"].max}
                          step={INPUT_LIMITS["slider-bullet-opacity"].step}
                          value={paginationInactiveOpacity}
                          onChange={(val) => {
                            setProp((props) => {
                              props.paginationInactiveOpacity = val;
                            });
                          }}
                          label={"Inactive Opacity"}
                        />
                      </>
                    ) : paginationType === "pagination-fraction" ? (
                      <UnitInput
                        resetValue={undefined}
                        isResponsive={true}
                        valueTablet={paginationFractionSizeTablet}
                        valueMobile={paginationFractionSizeMobile}
                        value={paginationFractionSize}
                        onChange={(val) => {
                          setProp((props) => {
                            props.paginationFractionSize = val;
                          });
                        }}
                        setValueTablet={(val) => {
                          setProp((props) => {
                            props.paginationFractionSizeTablet = val;
                          });
                        }}
                        setValueMobile={(val) => {
                          setProp((props) => {
                            props.paginationFractionSizeMobile = val;
                          });
                        }}
                        label={"Size"}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </EditPopover>
            </>
          )}
          <ToggleInput
            resetValue={false}
            value={hasEffect}
            onChange={(val) => {
              if (!val) {
                setProp((props) => {
                  props.effectType = undefined;
                  props.grabCursor = undefined;
                  props.cubeEffectShadow = true;
                  props.cubeEffectSlideShadows = true;
                  props.coverflowEffectRotate = 50;
                  props.coverflowEffectSlideShadows = true;
                  props.creativeEffectPrevShadow = true;
                  props.creativeEffectNextShadow = true;
                });
              }
              setProp((props) => {
                if (props.slidePerView && props.slidePerView > 1) {
                  props.slidePerView = 1;
                }
                if (props.slidePerViewTablet && props.slidePerViewTablet > 1) {
                  props.slidePerViewTablet = 1;
                }
                if (props.slidePerViewMobile && props.slidePerViewMobile > 1) {
                  props.slidePerViewMobile = 1;
                }
                props.hasEffect = val;
              });
            }}
            label={"Has Animation Effects"}
          />

          {hasEffect && (
            <>
              <EditPopover
                label={"Effect"}
                contentWidth={300}
                canReset={
                  effectType ||
                  grabCursor ||
                  cubeEffectShadow !== true ||
                  cubeEffectSlideShadows !== true ||
                  coverflowEffectRotate !== 50 ||
                  coverflowEffectSlideShadows !== true ||
                  creativeEffectPrevShadow !== true ||
                  creativeEffectNextShadow !== true
                }
                onReset={() => {
                  setProp((props) => {
                    props.effectType = undefined;
                    props.grabCursor = undefined;
                    props.cubeEffectShadow = true;
                    props.cubeEffectSlideShadows = true;
                    props.coverflowEffectRotate = 50;
                    props.coverflowEffectSlideShadows = true;
                    props.creativeEffectPrevShadow = true;
                    props.creativeEffectNextShadow = true;
                  });
                }}
              >
                <SelectInput
                  resetValue={undefined}
                  value={effectType}
                  onChange={(val) => {
                    setProp((props) => {
                      props.effectType = val;
                    });
                  }}
                  options={[
                    { label: "Fade", value: "fade" },
                    { label: "Cube", value: "cube" },
                    { label: "Coverflow", value: "coverflow" },
                    { label: "Flip", value: "flip" },
                    { label: "Cards", value: "cards" },
                    { label: "Creative", value: "creative" },
                    // { label: "Creative v1", value: "creative-v1" },
                    // { label: "Creative v2", value: "creative-v2" },
                    // { label: "Creative v3", value: "creative-v3" },
                    // { label: "Creative v4", value: "creative-v4" },
                    // { label: "Creative v5", value: "creative-v5" },
                  ]}
                  label="Effect Type"
                />
                <ToggleInput
                  resetValue={false}
                  value={grabCursor}
                  onChange={(val) => {
                    setProp((props) => {
                      props.grabCursor = val;
                    });
                  }}
                  label={"Grab Cursor"}
                />
                {effectType === "cube" && (
                  <>
                    <ToggleInput
                      resetValue={true}
                      value={cubeEffectShadow}
                      onChange={(val) => {
                        setProp((props) => {
                          props.cubeEffectShadow = val;
                        });
                      }}
                      label={"Shadow"}
                    />
                    <ToggleInput
                      resetValue={true}
                      value={cubeEffectSlideShadows}
                      onChange={(val) => {
                        setProp((props) => {
                          props.cubeEffectSlideShadows = val;
                        });
                      }}
                      label={"Slide Shadows"}
                    />
                  </>
                )}
                {effectType === "coverflow" && (
                  <>
                    <NumberInput
                      resetValue={50}
                      min={INPUT_LIMITS["slider-coverflow-rotate"].min}
                      max={INPUT_LIMITS["slider-coverflow-rotate"].max}
                      step={INPUT_LIMITS["slider-coverflow-rotate"].step}
                      value={coverflowEffectRotate}
                      onChange={(val) => {
                        setProp((props) => {
                          props.coverflowEffectRotate = val;
                        });
                      }}
                      label={"Rotate"}
                    />
                    <ToggleInput
                      resetValue={true}
                      value={coverflowEffectSlideShadows}
                      onChange={(val) => {
                        setProp((props) => {
                          props.coverflowEffectSlideShadows = val;
                        });
                      }}
                      label={"Slide Shadows"}
                    />
                  </>
                )}
                {effectType === "creative" && (
                  <>
                    <ToggleInput
                      resetValue={true}
                      value={creativeEffectPrevShadow}
                      onChange={(val) => {
                        setProp((props) => {
                          props.creativeEffectPrevShadow = val;
                        });
                      }}
                      label={"Prev Shadow"}
                    />
                    <ToggleInput
                      resetValue={true}
                      value={creativeEffectNextShadow}
                      onChange={(val) => {
                        setProp((props) => {
                          props.creativeEffectNextShadow = val;
                        });
                      }}
                      label={"Next Shadow"}
                    />
                  </>
                )}
              </EditPopover>
            </>
          )}

          <ToggleInput
            resetValue={false}
            value={hasAutoplay}
            onChange={(val) => {
              setProp((props) => {
                props.hasAutoplay = val;
                props.autoplayDelay = 3000;
                props.disableAutoplayOnInteraction = undefined;
              });
            }}
            label={"Has Autoplay"}
          />

          {hasAutoplay && (
            <>
              <EditPopover
                label={"Autoplay"}
                contentWidth={250}
                canReset={
                  autoplayDelay !== 3000 || disableAutoplayOnInteraction
                }
                onReset={() => {
                  setProp((props) => {
                    props.autoplayDelay = 3000;
                    props.disableAutoplayOnInteraction = undefined;
                  });
                }}
              >
                <NumberInput
                  min={INPUT_LIMITS["slider-delay"].min}
                  max={INPUT_LIMITS["slider-delay"].max}
                  resetValue={3000}
                  value={autoplayDelay}
                  onChange={(val) => {
                    setProp((props) => {
                      props.autoplayDelay = val;
                    });
                  }}
                  label={"Delay (ms)"}
                />
                <ToggleInput
                  resetValue={false}
                  value={disableAutoplayOnInteraction}
                  onChange={(val) => {
                    setProp((props) => {
                      props.disableAutoplayOnInteraction = val;
                    });
                  }}
                  label={"Disable On Interaction"}
                />
              </EditPopover>
            </>
          )}
          {!hasEffect && (
            <NumberInput
              isResponsive={true}
              resetValue={1}
              resetValueTablet={1}
              resetValueMobile={1}
              min={1}
              max={totalSlides}
              step={1}
              value={slidePerView}
              valueTablet={slidePerViewTablet}
              valueMobile={slidePerViewMobile}
              onChange={(val) => {
                setProp((props) => {
                  props.slidePerView = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.slidePerViewTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.slidePerViewMobile = val;
                });
              }}
              label={"Slide Per View"}
            />
          )}

          <ToggleInput
            resetValue={false}
            value={isCentered}
            onChange={(val) => {
              setProp((props) => {
                props.isCentered = val;
              });
            }}
            label={"Center Slide"}
          />

          <ToggleInput
            resetValue={false}
            value={isFreeMode}
            onChange={(val) => {
              setProp((props) => {
                props.isFreeMode = val;
              });
            }}
            label={"Free Mode"}
          />
          <ToggleInput
            resetValue={false}
            value={isKeyboardChange}
            onChange={(val) => {
              setProp((props) => {
                props.isKeyboardChange = val;
              });
            }}
            label={"Change via Keyboard"}
          />
          <ToggleInput
            resetValue={false}
            value={hasMouseWheel}
            onChange={(val) => {
              setProp((props) => {
                props.hasMouseWheel = val;
              });
            }}
            label={"Mouse Wheel"}
          />
          <ToggleInput
            resetValue={false}
            value={hasScrollbar}
            onChange={(val) => {
              setProp((props) => {
                props.hasScrollbar = val;
              });
            }}
            label={"Scrollbar"}
          />
          {(paginationType === "pagination-default" ||
            (!paginationType && !navigation)) && (
            <>
              <ToggleInput
                resetValue={false}
                value={isVertical}
                onChange={(val) => {
                  setProp((props) => {
                    if (val) {
                      props.navigation = false;
                    }
                    props.isVertical = val;
                  });
                }}
                label={"Vertical"}
              />
            </>
          )}
          <ToggleInput
            resetValue={false}
            value={hasStaticSlide}
            onChange={(val) => {
              setProp((props) => {
                props.hasStaticSlide = val;
              });
            }}
            label={"Static Slide"}
          />
          <ToggleInput
            resetValue={false}
            value={hasParallax}
            onChange={(val) => {
              setProp((props) => {
                props.hasParallax = val;
              });
            }}
            label={"Parallax"}
          />
          {hasParallax && (
            <>
              <div className="flex items-center justify-center  cursor-pointer duration-300 hover:bg-editor-bg-dark mb-4">
                <SourceImage
                  projectID={projectID}
                  src={parallaxBackground?.src}
                  srcMode={parallaxBackground?.srcMode}
                  onClick={() => {
                    setParallaxImagePickerModal(true);
                  }}
                />
              </div>
              <ImagePickerUIKit
                src={parallaxBackground?.src}
                srcMode={parallaxBackground?.srcMode}
                onChange={(src, srcMode) => {
                  setProp((props) => {
                    props.parallaxBackground = {
                      ...(props.parallaxBackground
                        ? props.parallaxBackground
                        : {}),
                      ...(props.parallaxBackground
                        ? props.parallaxBackground
                        : {}),
                      src: src,
                      srcMode: srcMode,
                    };
                  });
                }}
                opened={parallaxImagePickerModal}
                setOpened={setParallaxImagePickerModal}
              />
            </>
          )}
          <UnitInput
            auto={true}
            value={layoutWidth}
            isResponsive={true}
            valueTablet={layoutWidthTablet}
            valueMobile={layoutWidthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutWidthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            auto={true}
            resetValue={"80vh"}
            resetValueMobile={"80vh"}
            resetValueTablet={"80vh"}
            value={layoutHeight}
            isResponsive={true}
            valueTablet={layoutHeightTablet}
            valueMobile={layoutHeightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutHeightMobile = val;
              });
            }}
            label={"Height"}
          />
          <SelectInput
            resetValue={undefined}
            value={overflow}
            onChange={(val) => {
              setProp((props) => {
                props.overflow = val;
              });
            }}
            options={[
              { label: "Visible", value: "visible" },
              { label: "Hidden", value: "hidden" },
              { label: "Clip", value: "clip" },
              { label: "Scroll", value: "scroll" },
              { label: "Auto", value: "auto" },
              { label: "Inherit", value: "inherit" },
              { label: "Initial", value: "initial" },
              { label: "Revert", value: "revert" },
              { label: "Revert-layer", value: "revert-layer" },
              { label: "Unset", value: "unset" },
            ]}
            label="Overflow"
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width },
            { key: widthMobile },
            { key: widthTablet },
            { key: height },
            { key: heightMobile },
            { key: heightTablet },
          ])}
          id={`block-setting-${id}-slider-pagination`}
          title={"Inner Slider"}
          initialOpen={false}
        >
          <UnitInput
            auto={true}
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            auto={true}
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            resetValue={"100%"}
            resetValueTablet={"100%"}
            resetValueMobile={"100%"}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const paginationTabs = [
      {
        label: "Active",
        key: "active",
        children: activePaginationSettings(),
      },
      {
        label: "Inactive",
        key: "inactive",
        children: inactivePaginationSettings(),
      },
    ];
    function inactivePaginationSettings() {
      return (
        <>
          <ColorInput
            value={paginationInactiveColor}
            onChange={(val) => {
              setProp((props) => {
                props.paginationInactiveColor = val;
              });
            }}
            label={"Color"}
          />
        </>
      );
    }
    function activePaginationSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            value={paginationActiveColor}
            onChange={(val) => {
              setProp((props) => {
                props.paginationActiveColor = val;
              });
            }}
            label={"Color"}
          />
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: navigationButtonColor },
            { key: navigationButtonBackgroundColor },
          ])}
          id={`block-setting-${id}-slider-pagination`}
          title={"Navigation"}
          initialOpen={false}
        >
          <ColorInput
            value={navigationButtonColor}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonColor = val;
              });
            }}
            label={"Color"}
          />

          <ColorInput
            value={navigationButtonBackgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonBackgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </Panel>
        {((hasPagination && paginationType === "pagination-default") ||
          !paginationType ||
          isVertical) && (
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: paginationInactiveColor },
              { key: paginationActiveColor },
            ])}
            id={`block-setting-${id}-slider-pagination`}
            title={"Pagination"}
            initialOpen={false}
          >
            <TabsV2 items={paginationTabs} />
          </Panel>
        )}
        {hasPagination && paginationType === "pagination-fraction" && (
          <Panel
            isUpdated={isUpdatedFromDefault([{ key: paginationFractionColor }])}
            id={`block-setting-${id}-slider-pagination-fraction`}
            title={"Pagination"}
            initialOpen={false}
          >
            <ColorInput
              value={paginationFractionColor}
              onChange={(val) => {
                setProp((props) => {
                  props.paginationFractionColor = val;
                });
              }}
              label={"Color"}
            />
          </Panel>
        )}
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: navigationButtonPadding },
            { key: navigationButtonPaddingTablet },
            { key: navigationButtonPaddingMobile },
            { key: navigationButtonBorder },
            { key: navigationButtonBorderRadius },
          ])}
          id={`block-setting-${id}-slider-navigation`}
          title={"Navigation"}
          initialOpen={false}
        >
          <MarginInput
            value={navigationButtonMargin}
            resetValue={undefined}
            isResponsive={true}
            valueTablet={navigationButtonMarginTablet}
            valueMobile={navigationButtonMarginMobile}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.navigationButtonMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.navigationButtonMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            isResponsive={true}
            value={navigationButtonPadding}
            valueTablet={navigationButtonPaddingTablet}
            valueMobile={navigationButtonPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.navigationButtonPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.navigationButtonPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
          <BorderInput
            resetValue={undefined}
            value={navigationButtonBorder}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={navigationButtonBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.navigationButtonBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        {hasPagination &&
          (paginationType === "pagination-default" || !paginationType) && (
            <>
              <Panel
                isUpdated={isUpdatedFromDefault([
                  { key: paginationBorder },
                  { key: paginationBorderRadius },
                ])}
                id={`block-setting-${id}-slider-navigation-border`}
                title={"Pagination"}
                initialOpen={false}
              >
                <MarginInput
                  resetValue={undefined}
                  isResponsive={true}
                  value={paginationMargin}
                  valueTablet={paginationMarginTablet}
                  valueMobile={paginationMarginMobile}
                  onChange={(val) => {
                    setProp((props) => {
                      props.paginationMargin = val;
                    });
                  }}
                  setValueTablet={(val) => {
                    setProp((props) => {
                      props.paginationMarginTablet = val;
                    });
                  }}
                  setValueMobile={(val) => {
                    setProp((props) => {
                      props.paginationMarginMobile = val;
                    });
                  }}
                  label={"Margin"}
                />
                <PaddingInput
                  resetValue={undefined}
                  isResponsive={true}
                  value={paginationPadding}
                  valueTablet={paginationPaddingTablet}
                  valueMobile={paginationPaddingMobile}
                  onChange={(val) => {
                    setProp((props) => {
                      props.paginationPadding = val;
                    });
                  }}
                  setValueTablet={(val) => {
                    setProp((props) => {
                      props.paginationPaddingTablet = val;
                    });
                  }}
                  setValueMobile={(val) => {
                    setProp((props) => {
                      props.paginationPaddingMobile = val;
                    });
                  }}
                  label={"Padding"}
                />
                <BorderInput
                  resetValue={undefined}
                  value={paginationBorder}
                  onChange={(val) => {
                    setProp((props) => {
                      props.paginationBorder = val;
                    });
                  }}
                  label={"Border"}
                />
                <BorderRadiusInput
                  resetValue={undefined}
                  value={paginationBorderRadius}
                  onChange={(val) => {
                    setProp((props) => {
                      props.paginationBorderRadius = val;
                    });
                  }}
                  label={"Border Radius"}
                />
              </Panel>
            </>
          )}
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: totalSlides, default: 3 },
        { key: navigation },
        { key: loop },
        { key: spaceBetween },
        { key: spaceBetweenTablet },
        { key: spaceBetweenMobile },
        { key: sliderSpeed, default: 300 },
        { key: hasTouchMove, default: false },
        { key: hasPagination, default: true },
        { key: paginationType },
        { key: hasDynamicBullets },
        { key: isClickable },
        { key: alignPagination },
        { key: alignPaginationMobile },
        { key: alignPaginationTablet },
        { key: paginationSize },
        { key: paginationSizeTablet },
        { key: paginationSizeMobile },
        { key: paginationGap },
        { key: paginationGapTablet },
        { key: paginationGapMobile },
        { key: paginationActiveOpacity, default: 1 },
        { key: paginationInactiveOpacity, default: 0.2 },
        { key: paginationFractionSizeTablet },
        { key: paginationFractionSizeMobile },
        { key: paginationFractionSize },
        { key: hasEffect },
        { key: effectType },
        { key: grabCursor },
        { key: hasAutoplay },
        { key: autoplayDelay, default: 3000 },
        { key: disableAutoplayOnInteraction },
        { key: slidePerView },
        { key: isCentered },
        { key: isFreeMode },
        { key: isKeyboardChange },
        { key: hasMouseWheel },
        { key: hasScrollbar },
        { key: navigation, default: true },
        { key: hasStaticSlide },
        { key: hasParallax },
        { key: parallaxBackground },
        { key: width },
        { key: widthTablet },
        { key: widthMobile },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: navigationWidth },
        { key: navigationWidthTablet },
        { key: navigationWidthMobile },
        { key: navigationHeight },
        { key: navigationHeightTablet },
        { key: navigationHeightMobile },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: paginationInactiveColor },
        { key: paginationActiveColor },
        { key: navigationButtonColor },
        { key: navigationButtonBackgroundColor },
        { key: paginationFractionColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: navigationButtonPadding },
        { key: navigationButtonPaddingTablet },
        { key: navigationButtonPaddingMobile },
        { key: navigationButtonBorder },
        { key: navigationButtonBorderRadius },
        { key: paginationBorderRadius },
        { key: paginationBorder },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        key={`block-settings-tab-${id}`}
        items={tabs}
        id={`block-settings-tab-${id}`}
      />
    </div>
  );
};
